/**
 * 확인 창 — 브라우저 기본 확인 창(`window.confirm`) 대신 쓰는 콘솔의 창.
 *
 * 기본 확인 창은 콘솔과 생김이 달라 "사이트가 띄운 경고" 처럼 보이고, 무엇을 지우는지 굵게 보일 수도,
 * 위험한 단추를 빨갛게 칠할 수도 없다. 단추 이름도 늘 "확인 · 취소" 라 [확인] 이 지우는 것인지 닫는
 * 것인지 읽어야 안다. 여기서는 단추가 할 일을 말하고("삭제", "저장하지 않고 닫기"), 위험한 일은
 * 처음 초점을 [취소] 에 둔다 — Enter 한 번에 지워지지 않게.
 *
 * 부르는 쪽은 `await confirmDialog({...})` 한 줄이다. 창은 앱 맨 위의 `ConfirmHost` 가 그린다.
 */
import { useSyncExternalStore, type ReactNode } from 'react';
import { Modal } from './ui.js';

export interface ConfirmOptions {
  title: string;
  /** 무엇이 일어나는지. 대상 이름은 `<strong>` 으로 감싸 보이게 한다. */
  body?: ReactNode;
  /** 할 일을 말하는 단추 이름 — "삭제", "중지". 기본 "확인". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** 되돌릴 수 없는 일 — 빨간 단추, 처음 초점은 [취소]. */
  danger?: boolean;
}

interface Pending extends ConfirmOptions {
  id: number;
  resolve: (ok: boolean) => void;
}

let queue: readonly Pending[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** 묻고 답을 기다린다. 창이 이미 떠 있으면 그 뒤에 줄을 선다. */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    queue = [...queue, { ...options, id: nextId++, resolve }];
    emit();
  });
}

function answer(ok: boolean): void {
  const [head, ...rest] = queue;
  if (!head) return;
  queue = rest;
  emit();
  head.resolve(ok);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 앱에 한 번 둔다. 로그인 전후 어느 화면에서 불러도 같은 자리에 뜬다. */
export function ConfirmHost(): ReactNode {
  const current = useSyncExternalStore(subscribe, () => queue[0] ?? null);
  if (!current) return null;
  return (
    // 줄 선 다음 질문이 같은 창을 물려받지 않게 — 질문마다 창을 새로 연다(초점·되돌릴 자리가 새로 잡힌다).
    <Modal key={current.id} title={current.title} onClose={() => answer(false)}>
      <div className="confirm">
        {current.body ? <div className="confirm-body">{current.body}</div> : null}
        <div className="form-actions">
          <button
            type="button"
            className="btn"
            data-autofocus={current.danger === true ? '' : undefined}
            onClick={() => answer(false)}
          >
            {current.cancelLabel ?? '취소'}
          </button>
          <button
            type="button"
            className={`btn ${current.danger ? 'btn-danger' : 'btn-primary'}`}
            data-autofocus={current.danger !== true ? '' : undefined}
            onClick={() => answer(true)}
          >
            {current.confirmLabel ?? '확인'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
